'use client';

import { ParentSize } from '@visx/responsive';
import { type RefObject, useContext, useEffect } from 'react';
import type { ChartValues } from '@/types/chart';
import { cn } from '@/utils/ui-utils';
import { VisualMenuContext } from '../VisualMenu/provider/VisualMenuContext';
import { NoData } from './modules/NoData';
import ChartError from './modules/VisualError';
import ChartSkeleton from './modules/VisualSkeleton';

interface VisualContainerProps {
  data: ChartValues[][] | ChartValues[];
  isLoading?: boolean;
  isError?: boolean;
  onErrorHandler?: () => void;
  height?: number;
  legendHeight?: number;
  ref?: RefObject<HTMLDivElement | null>;
  className?: string;
  showDataOverYear?: boolean;
  customSkeleton?: React.ReactNode | React.ReactNode[];
  noDataMessage?: string;
  children: (args: { width: number }) => React.ReactNode;
}

export const VisualContainer = ({
  ref,
  data,
  isLoading = false,
  isError,
  onErrorHandler,
  height = 340,
  legendHeight = 24,
  className,
  customSkeleton,
  noDataMessage,
  children,
}: VisualContainerProps) => {
  const handleLatestDataPoint = useContext(VisualMenuContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `data` is an array prop that's typically a fresh reference each render, so we key this effect to its content via JSON.stringify — depending on the `data` reference would re-invoke handleLatestDataPoint (which sets state) every render and risk a loop.
  useEffect(() => {
    if (!handleLatestDataPoint) return;

    handleLatestDataPoint(data);
  }, [JSON.stringify(data), handleLatestDataPoint]);

  const series = data.flat();
  const noData =
    series.length === 0 || series.every((d) => d.data.length === 0 || d.data.every((p) => p.value === null));
  return (
    <div className={cn('relative mt-0.5 h-full w-full px-5 pb-5', className)}>
      <ParentSize>
        {({ width }) => (
          <div ref={ref}>
            {isLoading ? (
              customSkeleton ? (
                customSkeleton
              ) : (
                <ChartSkeleton height={height + legendHeight} />
              )
            ) : isError ? (
              <ChartError
                height={height + legendHeight}
                onClick={onErrorHandler}
              />
            ) : noData ? (
              <NoData
                width={width}
                height={height + legendHeight}
                message={noDataMessage}
              />
            ) : (
              <>
                {/* Do not remove when exporting to png/ svg HTML gets injected here */}
                <div
                  id='export-container'
                  className='hidden'
                  style={{ width }}
                />
                {children({ width: width })}
              </>
            )}
          </div>
        )}
      </ParentSize>
    </div>
  );
};

VisualContainer.displayName = 'VisualContainer';
